import { useState, useEffect } from 'react';
import { 
  HelpCircle, Search, X, ChevronRight, ChevronDown, 
  FileCheck, FileText, CreditCard, Settings, MessageCircle,
  BookOpen, Video, ExternalLink
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import axios from 'axios';

// Icon mapping for categories
const ICON_MAP = {
  HelpCircle: HelpCircle,
  FileCheck: FileCheck,
  FileText: FileText,
  CreditCard: CreditCard,
  Settings: Settings,
  MessageCircle: MessageCircle,
  BookOpen: BookOpen,
  Video: Video,
};

export default function InstantHelper() {
  const [isOpen, setIsOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [faqItems, setFaqItems] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [expandedItems, setExpandedItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchCategories();
      fetchAllFaqs();
    }
  }, [isOpen]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get('/faq/categories');
      setCategories(res.data || []);
    } catch (error) {
      console.error('Failed to load FAQ categories:', error);
    }
  };

  const fetchAllFaqs = async () => {
    try {
      const res = await axios.get('/faq/items');
      setFaqItems(res.data || []);
    } catch (error) {
      console.error('Failed to load FAQs:', error);
    }
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.get(`/faq/search?q=${encodeURIComponent(query)}`);
      setSearchResults(res.data || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (itemId) => {
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  const getFilteredItems = () => {
    if (searchQuery.length >= 2) {
      return searchResults;
    }
    if (selectedCategory) {
      return faqItems.filter(item => item.category === selectedCategory);
    }
    return faqItems;
  };

  const getCategoryIcon = (iconName) => {
    const Icon = ICON_MAP[iconName] || HelpCircle;
    return Icon;
  };

  const getCategoryName = (categoryId) => {
    const cat = categories.find(c => c.id === categoryId);
    return cat?.name || categoryId;
  };

  return (
    <>
      {/* Floating Help Button - positioned in bottom bar area */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-4 left-20 z-[60] bg-indigo-600 hover:bg-indigo-700 text-white rounded-full p-3 shadow-lg hover:shadow-xl transition-all duration-300 group"
        data-testid="instant-helper-trigger"
        title="Need Help?"
      >
        <HelpCircle className="w-5 h-5" />
      </button>

      {/* Help Panel Overlay */}
      {isOpen && (
        <div className="fixed inset-0 z-[70] flex justify-end" data-testid="instant-helper-panel">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setIsOpen(false)}
          />
          
          {/* Panel */}
          <div className="relative w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-in-right">
            {/* Header */}
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <HelpCircle className="w-6 h-6" />
                  <h2 className="text-lg font-semibold">Instant Help</h2>
                </div>
                <button 
                  onClick={() => setIsOpen(false)}
                  className="hover:bg-white/20 rounded-full p-1 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300" />
                <Input
                  placeholder="Search for help..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-indigo-200 focus:bg-white/20"
                />
              </div>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1">
              <div className="p-4 space-y-4">
                {/* Categories (shown when not searching) */}
                {searchQuery.length < 2 && !selectedCategory && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-500 mb-3">Categories</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {categories.map(cat => {
                        const Icon = getCategoryIcon(cat.icon);
                        const itemCount = faqItems.filter(i => i.category === cat.id).length;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => setSelectedCategory(cat.id)}
                            className="flex items-center gap-3 p-3 bg-slate-50 hover:bg-indigo-50 rounded-lg transition-colors text-left group"
                          >
                            <div className="bg-indigo-100 group-hover:bg-indigo-200 rounded-lg p-2 transition-colors">
                              <Icon className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-medium text-slate-800 text-sm">{cat.name}</p>
                              <p className="text-xs text-slate-500">{itemCount} articles</p>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Back button when category selected */}
                {selectedCategory && (
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className="flex items-center gap-2 text-indigo-600 hover:text-indigo-800 text-sm font-medium"
                  >
                    <ChevronRight className="w-4 h-4 rotate-180" />
                    Back to Categories
                  </button>
                )}

                {/* Search Results or Category Items */}
                {(searchQuery.length >= 2 || selectedCategory) && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-500 mb-3">
                      {searchQuery.length >= 2 
                        ? `Search Results (${getFilteredItems().length})`
                        : getCategoryName(selectedCategory)
                      }
                    </h3>
                    
                    {loading ? (
                      <div className="text-center py-8 text-slate-500">
                        Searching...
                      </div>
                    ) : getFilteredItems().length === 0 ? (
                      <div className="text-center py-8 text-slate-500">
                        <HelpCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                        <p>No FAQs found</p>
                        <p className="text-sm mt-1">Try a different search term</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {getFilteredItems().map(item => (
                          <div 
                            key={item.id}
                            className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                          >
                            <button
                              onClick={() => toggleItem(item.id)}
                              className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50 transition-colors"
                            >
                              <span className="font-medium text-slate-800 text-sm pr-2">
                                {item.question}
                              </span>
                              {expandedItems[item.id] ? (
                                <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                              )}
                            </button>
                            {expandedItems[item.id] && (
                              <div className="px-3 pb-3 text-sm text-slate-600 border-t border-slate-100 pt-3 bg-slate-50">
                                {item.answer}
                                {searchQuery.length >= 2 && (
                                  <Badge className="mt-2 bg-indigo-100 text-indigo-700 text-xs">
                                    {getCategoryName(item.category)}
                                  </Badge>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Quick FAQs (when no category selected and not searching) */}
                {searchQuery.length < 2 && !selectedCategory && faqItems.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-slate-500 mb-3">Frequently Asked</h3>
                    <div className="space-y-2">
                      {faqItems.slice(0, 5).map(item => (
                        <div 
                          key={item.id}
                          className="bg-white border border-slate-200 rounded-lg overflow-hidden"
                        >
                          <button
                            onClick={() => toggleItem(item.id)}
                            className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50 transition-colors"
                          >
                            <span className="font-medium text-slate-800 text-sm pr-2">
                              {item.question}
                            </span>
                            {expandedItems[item.id] ? (
                              <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            )}
                          </button>
                          {expandedItems[item.id] && (
                            <div className="px-3 pb-3 text-sm text-slate-600 border-t border-slate-100 pt-3 bg-slate-50">
                              {item.answer}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Contact Support */}
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg p-4 border border-indigo-100">
                  <h3 className="font-medium text-indigo-900 mb-2">Still need help?</h3>
                  <p className="text-sm text-indigo-700 mb-3">
                    Our support team is here to assist you
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700 text-xs">
                      <MessageCircle className="w-3 h-3 mr-1" />
                      Contact Support
                    </Button>
                    <Button size="sm" variant="outline" className="text-xs border-indigo-200 text-indigo-700">
                      <ExternalLink className="w-3 h-3 mr-1" />
                      Documentation
                    </Button>
                  </div>
                </div>
              </div>
            </ScrollArea>
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style jsx global>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </>
  );
}
